import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Close from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Person from '@mui/icons-material/Person';
import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import useAuth from '../../authentication/useAuth';

const useStyle = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
	},
	contact: {
		backgroundColor: theme.palette.grey[100],
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3),
	},
	contactCard: {
		borderTop: `1px solid ${theme.palette.grey[400]}`,
	},
	accessTable: {},
}));

const StyledTableCell = withStyles((theme) => ({
	head: {
		backgroundColor: theme.palette.white.colorWhite,
	},
	body: {},
}))(TableCell);

const MaxWidthTableCell = withStyles((theme) => ({
	body: {
		maxWidth: theme.spacing(22),
	},
}))(TableCell);

function CustomerAccounts({
	customerEmail,
	isOpen,
	onClose,
	namespace,
	accountDetail,
	isLoading,
}) {
	const classes = useStyle();
	const { t } = useTranslation(namespace, 'common', 'account');
	const navigate = useNavigate();
	const { isImpersonating, endImpersonation, impersonate, getCurrentUser } =
		useAuth();
	const rawAccessRows =
		accountDetail?.access ??
		accountDetail?.Access ??
		accountDetail?.accesses ??
		accountDetail?.Accesses;
	const accessRows = Array.isArray(rawAccessRows) ? rawAccessRows : [];

	const handleClose = () => {
		onClose();
	};

	const startImpersonate = async (email) => {
		await impersonate(email);
		handleClose();
		navigate('/');
	};

	const endImpersonate = async () => {
		await endImpersonation();
		handleClose();
	};

	const getAccessValue = (access, keys) => {
		for (let index = 0; index < keys.length; index += 1) {
			const value = access?.[keys[index]];
			if (value !== undefined && value !== null) {
				return value;
			}
		}
		return '';
	};

	const getCanSeePriceValue = (access) => {
		const rawValue = getAccessValue(access, [
			'canSeePrice',
			'CanSeePrice',
			'can_see_price',
		]);

		if (typeof rawValue === 'string') {
			const normalized = rawValue.trim().toLowerCase();
			return normalized === 'yes' || normalized === 'true';
		}

		return Boolean(rawValue);
	};

	return (
		<Drawer
			anchor="right"
			BackdropProps={{ invisible: true }}
			className={classes.root}
			ModalProps={{ BackdropProps: { invisible: true } }}
			PaperProps={{
				sx: {
					width: { xs: '100%', sm: 'min(92vw, 760px)' },
					maxWidth: '100vw',
				},
			}}
			onClose={handleClose}
			open={isOpen}
			variant="temporary"
		>
			{isLoading ? (
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					paddingTop={4}
					paddingBottom={4}
				>
					<CircularProgress />
				</Box>
			) : null}
			<Box component="span" textAlign="right">
				<IconButton
					disableRipple
					onClick={handleClose}
					aria-label="close"
					role="button"
					aria-hidden="false"
				>
					<Close
						fontSize="large"
						aria-label="close"
						role="graphics-symbol"
						aria-hidden="true"
					/>
				</IconButton>
			</Box>
			<Box paddingLeft={3} paddingRight={3} paddingTop={1}>
				<Typography variant="heading2" className="customerAccount-fullName">
					{accountDetail?.fullName}
				</Typography>
			</Box>
			<Box paddingLeft={3} paddingRight={3} paddingTop={2} paddingBottom={2}>
				{isImpersonating && getCurrentUser().email === customerEmail ? (
					<Button
						variant="contained"
						color="primary"
						size="large"
						role="button"
						data-testid={t('end_impersonation')}
						aria-label={t('end_impersonation')}
						onClick={() => endImpersonate()}
					>
						{t('end_impersonation')}
					</Button>
				) : (
					<Button
						variant="contained"
						color="primary"
						size="large"
						role="button"
						data-testid={t('start_impersonation')}
						aria-label={t('start_impersonation')}
						onClick={() => startImpersonate(customerEmail)}
					>
						{t('start_impersonation')}
					</Button>
				)}
			</Box>
			<div className={classes.contact}>
				<Box
					display="flex"
					flexDirection="row"
					paddingLeft={3}
					paddingRight={3}
					paddingTop={2}
				>
					<Person color="disabled" />
					<Box paddingLeft={1}>
						<Typography
							variant="heading6"
							style={{ textTransform: 'upperCase' }}
						>
							{t('contact_information')}
						</Typography>
					</Box>
				</Box>
				<Box
					display="flex"
					flexGrow={1}
					justifyContent="left"
					flexDirection="row"
					paddingLeft={3}
					paddingRight={3}
					paddingTop={2}
				>
					<Box
						borderTop={1}
						margin={1}
						className={classes.contactCard}
						flexGrow={1}
					>
						<Typography
							color="textSecondary"
							variant="caption"
							style={{ textTransform: 'upperCase' }}
						>
							{t('email')}
						</Typography>
						<Typography className="customerAccount-Email">
							{customerEmail}
						</Typography>
					</Box>
					<Box
						borderTop={1}
						margin={1}
						className={classes.contactCard}
						flexGrow={1}
					>
						<Typography
							color="textSecondary"
							variant="caption"
							style={{ textTransform: 'upperCase' }}
						>
							{t('company_name')}
						</Typography>
						<Typography className="customerAccount-companyName">
							{accountDetail?.companyName}
						</Typography>
					</Box>
					<Box
						borderTop={1}
						margin={1}
						className={classes.contactCard}
						flexGrow={1}
					>
						<Typography
							color="textSecondary"
							variant="caption"
							style={{ textTransform: 'upperCase' }}
						>
							{t('account:customer_number')}
						</Typography>
						<Typography className="customerAccount-customerNumber"></Typography>
					</Box>
				</Box>
			</div>

			<Box
				display="flex"
				flexDirection="row"
				paddingLeft={3}
				paddingRight={3}
				paddingTop={3}
			>
				<VerifiedUser color="disabled" />
				<Box paddingLeft={1}>
					<Typography variant="heading6" style={{ textTransform: 'upperCase' }}>
						{t('access_permissions')}
					</Typography>
				</Box>
			</Box>
			<Box
				display="flex"
				flexDirection="row"
				paddingLeft={3}
				paddingRight={0}
				paddingTop={2}
				overflowX="auto"
			>
				<Table
					stickyHeader
					className={classes.accessTable}
					sx={{ minWidth: 680 }}
				>
					<TableHead>
						<TableRow>
							<StyledTableCell>{t('bill_to')}</StyledTableCell>
							<StyledTableCell>{t('ship_to')}</StyledTableCell>
							<StyledTableCell>{t('plant')}</StyledTableCell>
							<StyledTableCell>{t('can_see_price')}</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{accessRows.map((access) => (
							<TableRow key={JSON.stringify(access)}>
								<MaxWidthTableCell component="th" scope="row">
									{getAccessValue(access, ['billTo', 'BillTo'])}
								</MaxWidthTableCell>
								<MaxWidthTableCell>
									{getAccessValue(access, ['shipTo', 'ShipTo'])}
								</MaxWidthTableCell>
								<MaxWidthTableCell>
									{getAccessValue(access, ['plant', 'Plant'])}
								</MaxWidthTableCell>
								<MaxWidthTableCell>
									{getCanSeePriceValue(access) ? t('yes') : t('no')}
								</MaxWidthTableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Drawer>
	);
}

CustomerAccounts.propTypes = {
	customerEmail: PropTypes.string,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	namespace: PropTypes.string,
	accountDetail: PropTypes.object,
	isLoading: PropTypes.bool,
};

CustomerAccounts.defaultProps = {
	accountDetail: {},
	isLoading: false,
};

export default CustomerAccounts;
